/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {CancellationToken, uncancelableToken} from 'neuroglancer/util/cancellation';
import {Uint64} from 'neuroglancer/util/uint64';
import { message } from '../widget/message';
import {interceptorHeaderInfo} from "neuroglancer/util/http_interceptors_base";

export const makeElementUtil = (
  tag: string,
  classes?: string[],
  attrs?: Record<string, any>,
  html?:string): HTMLElement => {
  const el:HTMLElement = document.createElement(tag) as HTMLElement;
  el.classList.add(...(classes ?? []));
  Object.entries(attrs ?? {})
      .forEach(([key, value]) => el.setAttribute(key, value));
  el.innerHTML = (html ?? '');
  return el;
};

export class HttpError extends Error {
  url: string;
  status: number;
  statusText: string;
  response?: Response;

  constructor(url: string, status: number, statusText: string, response?: Response) {
    let message = `Fetching ${JSON.stringify(url)} resulted in HTTP error ${status}`;
    if (statusText) {
      message += `: ${statusText}`;
    }
    message += '.';
    super(message);
    this.name = 'HttpError';
    this.message = message;
    this.url = url;
    this.status = status;
    this.statusText = statusText;
    if (response) {
      this.response = response;
    }
  }

  static fromResponse(response: Response) {
    return new HttpError(response.url, response.status, response.statusText, response);
  }

  static fromRequestError(input: RequestInfo, error: unknown) {
    if (error instanceof TypeError) {
      let url: string;
      if (typeof input === 'string') {
        url = input;
      } else {
        url = input.url;
      }
      return new HttpError(url, 0, 'Network or CORS error');
    }
    return error;
  }
}

/**
 * Issues a `fetch` request.
 *
 * If the request fails due to an HTTP status outside `[200, 300)`, throws an `HttpError`.  If the
 * request fails due to a network or CORS restriction, throws an `HttpError` with a `status` of `0`.
 */
export async function fetchOk(input: RequestInfo, init?: RequestInit): Promise<Response> {
  let response: Response;
  if (init?.headers) {
    init.headers = {...init.headers, ...interceptorHeaderInfo.value}
  } else if (init) {
    init.headers = interceptorHeaderInfo.value
  } else {
    init = {headers: interceptorHeaderInfo.value}
  }
  try {
    init.credentials = 'include';
    response = await fetch(input, init);
    if( response.status === 500 ){
      if(!response.url.includes('-') || !response.url.includes('_')){
        new message(`${response.statusText}`,'error');
      }
    }
  } catch (error) {
    console.log(error)
    new message(`${error}`, 'error');
    throw HttpError.fromRequestError(input, error);
  }
  if (!response.ok) throw HttpError.fromResponse(response);
  return response;
}

export function responseArrayBuffer(response: Response): Promise<ArrayBuffer> {
  return response.arrayBuffer();
}

export function responseJson(response: Response): Promise<any> {
  return response.json();
}

export type ResponseTransform<T> = (response: Response) => Promise<T>;

/**
 * Issues a `fetch` request in the same way as `fetchOk`, and returns the result of the promise
 * returned by `transformResponse`.
 *
 * Additionally, the request may be cancelled through `cancellationToken`.
 *
 * The `transformResponse` function should not do anything with the `Response` object after its
 * result becomes ready; otherwise, cancellation may not work as expected.
 */
export async function cancellableFetchOk<T>(
    input: RequestInfo, init: RequestInit, transformResponse: ResponseTransform<T>,
    cancellationToken: CancellationToken = uncancelableToken): Promise<T> {
  if (cancellationToken === uncancelableToken) {
    const response = await fetchOk(input, init);
    return await transformResponse(response);
  }
  const abortController = new AbortController();
  const unregisterCancellation = cancellationToken.add(() => abortController.abort());
  try {
    const response = await fetchOk(input, {...init, signal: abortController.signal});
    return await transformResponse(response);
  } finally {
    unregisterCancellation();
  }
}

const tempUint64 = new Uint64();

export function getByteRangeHeader(startOffset: Uint64|number, endOffset: Uint64|number) {
  let endOffsetStr: string;
  if (typeof endOffset === 'number') {
    endOffsetStr = `${endOffset - 1}`;
  } else {
    Uint64.decrement(tempUint64, endOffset);
    endOffsetStr = tempUint64.toString();
  }
  return {'Range': `bytes=${startOffset}-${endOffsetStr}`};
}

export function parseUrl(url: string): {protocol: string, host: string, path: string} {
  const urlProtocolPattern = /^([^:\/]+):\/\/([^\/]+)((?:\/.*)?)$/;
  let match = url.match(urlProtocolPattern);
  if (match === null) {
    new message(`Invalid URL: ${JSON.stringify(url)}`, 'error');
    throw new Error(`Invalid URL: ${JSON.stringify(url)}`);
  }
  return {protocol: match[1], host: match[2], path: match[3]};
}

export function parseStereoUrl(url:string): {protocol: string, host: string, path: string, sn:string, imgType:string, expType:string} {
  let u = parseUrl(url)
  const urlDNBPattern = /^\/([^\/]+)\/([^\/]+)\/([^\/]+)\/.*/;
  let match = u['path'].match(urlDNBPattern);
  if (match === null) {
    return {protocol: u['protocol'], host: u['host'], path: u['path'], sn:'', imgType:'', expType:''}
  }
  return {protocol: u['protocol'], host: u['host'], path: u['path'], sn: match[1], imgType: match[2], expType: match[3]};
}

export function isNotFoundError(e: any) {
  if (!(e instanceof HttpError)) return false;
  // Treat CORS errors (0) or 403 as not found.  S3 returns 403 if the file does not exist because
  // permissions are per-file.
  return (e.status === 0 || e.status === 403 || e.status === 404);
}

export function displayLoadingNone(){
  if(document.getElementsByClassName('el-loading-mask')[0]){
    (document.getElementsByClassName('el-loading-mask')[0] as HTMLElement).style.display = 'none';
    document.body.removeChild(document.getElementsByClassName('el-loading-mask')[0]);
  }
}

export function noPermision() {
  (document.body as HTMLElement).innerHTML = '';
  document.body?.appendChild(
    makeElementUtil('div', ['noPermision'], { 'style': 'background: rgb(31, 33, 50);text-align: center; position: relative; height:100%' },
      `<div style="margin: auto;position: absolute;top: 0;bottom: 0;left: 0;right: 0;height: 33rem;">
        <img style="height: 29rem;" src="./static/404.png" />
        <p style="font-size: 1.5em;font-family: MicrosoftYaHei; color: #999999;">You don't have access to this data.</p>
      </div>`)
  );
  new message((sessionStorage.getItem('lang') || sessionStorage.getItem('defaultLang')) === 'zh-CN' ? '登录信息过期，请重新登陆。' : 'No permission token, please login!', 'error');
}
